import { Button } from '@chakra-ui/button';
import Icon from '@chakra-ui/icon';
import { Divider, Flex, Heading } from '@chakra-ui/layout';
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/modal';
import { useToast } from '@chakra-ui/toast';
import moment from 'moment';
import 'moment/locale/de';
import React, { useEffect, useState } from 'react';
import { RotateCcw, Save } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useConfig } from '../../../../hooks/useConfig';
import {
  AdminCoursesModalForm,
  CourseCategory,
  FirebaseCourseWithUid,
} from '../../../../types/types';
import { createCourse } from '../../../../utils/createCourse';
import { editCourse } from '../../../../utils/editCourse';
import { AdminCoursesModalCategory } from './AdminCoursesModalCategory';
import { AdminCoursesModalCommon } from './AdminCoursesModalCommon';
import { AdminCoursesModalRecurrence } from './AdminCoursesModalRecurrence';
import { AdminCoursesModalUpload } from './AdminCoursesModalUpload';

interface AdminCoursesModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'edit' | 'create';
  firebaseCourse?: undefined | FirebaseCourseWithUid;
  categories: CourseCategory[];
}

export const AdminCoursesModal: React.FC<AdminCoursesModalProps> = ({
  isOpen,
  onClose,
  type,
  firebaseCourse,
  categories,
}) => {
  const form = useForm<AdminCoursesModalForm>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const firebaseConfig = useConfig();

  useEffect(() => {
    if (isOpen && firebaseConfig) {
      if (firebaseCourse && type === 'edit') {
        form.reset({
          title: firebaseCourse.title,
          description: firebaseCourse.description,
          startDate: moment(firebaseCourse.start).format('YYYY-MM-DD'),
          startTime: moment(firebaseCourse.start).format('HH:mm'),
          duration: firebaseCourse.duration.toString(),
          trainerId: firebaseCourse.trainer.ids[0],
          isRecurring: firebaseCourse.recurrence.isRecurring,
          isOnline: firebaseCourse.isOnline,
          courseCategoryId: firebaseCourse.courseCategoryId || '',
          daysOfWeek: firebaseCourse.recurrence.daysOfWeek,
          canRegister: !!firebaseCourse.canRegister,
          maxParticipants: firebaseCourse.maxParticipants,
          roomName: firebaseCourse.roomName,
        });
      } else {
        form.reset({
          title: '',
          description: '',
          startDate: moment().format('YYYY-MM-DD'),
          startTime: '',
          duration: '',
          trainerId: '',
          isRecurring: false,
          isOnline: true,
          courseCategoryId: '',
          daysOfWeek: new Array(7).fill(false),
          canRegister: true,
          maxParticipants: 10,
          roomName: '',
        });
      }
    }
  }, [type, firebaseCourse, isOpen]);

  const onSubmit = form.handleSubmit(async (course) => {
    setIsSubmitting(true);
    if (course.startDate === null) {
      toast({
        title: 'Fehler beim Speichern',
        description:
          'Du scheinst einen alten Browser zu nutzen. Bitte nutze Chrome oder Firefox, oder update Safari auf mind. 14.1.',
        status: 'error',
      });
      setIsSubmitting(false);
      return;
    }
    try {
      if (type === 'create') {
        await createCourse(course);
      } else if (type === 'edit' && firebaseCourse) {
        await editCourse(course, firebaseCourse);
      } else {
        throw new Error('unknown');
      }
      toast({
        title: 'Erfolgreich',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      setIsSubmitting(false);
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      toast({
        title: 'Ein Fehler ist aufgetreten.',
        description: 'Bitte versuche es später noch einmal.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent p={6} boxShadow="xl" borderRadius="xl">
        <ModalCloseButton />
        <Heading mb={6} size="md">
          Kurs {type === 'edit' ? 'bearbeiten' : 'planen'}
        </Heading>
        <form onSubmit={onSubmit}>
          <AdminCoursesModalCommon form={form} />
          <Divider my={6} />
          <AdminCoursesModalUpload
            form={form}
            firebaseCourse={firebaseCourse}
          />
          <Divider my={6} />
          <AdminCoursesModalRecurrence form={form} />
          <Divider my={6} />
          <AdminCoursesModalCategory form={form} categories={categories} />
          <Flex justify="flex-end">
            <Button leftIcon={<Icon as={RotateCcw} />} onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              ml={2}
              isLoading={isSubmitting}
              type="submit"
              leftIcon={<Icon as={Save} />}
              colorScheme="brand"
            >
              Speichern
            </Button>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
};
